import { ReactNode } from "react";

interface CTAButtonProps {
    children: ReactNode;
    variant?: "primary" | "secondary" | "outline" | "brand" | "neutral";
    size?: "sm" | "md" | "lg" | "brand_sm";
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
    href?: string;
}

export function CTAButton({
    children,
    variant = "outline",
    size = "md",
    onClick,
    disabled = false,
    className = "",
    href,
}: CTAButtonProps) {
    const baseClasses =
        "rounded-full flex items-center justify-center cursor-pointer transition-all duration-150 ease-in-out font-medium";

    const variantClasses = {
        primary:
            "bg-green-700 text-white border border-green-700 hover:bg-green-800 hover:border-green-800",
        secondary:
            "bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 hover:border-gray-400",
        outline:
            "bg-transparent text-gray-800 border-[1.5px] border-black hover:bg-green-50",
        brand: "bg-brand-500 text-white shadow-xs hover:bg-brand-500/90",
        neutral:
            "border bg-background text-emphasis shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
    };

    const sizeClasses = {
        sm: "h-10 px-4 text-sm",
        md: "h-13 px-6 text-lg",
        lg: "h-16 px-8 text-xl",
        brand_sm: "h-9 px-3 py-2",
    };

    const disabledClasses = disabled
        ? "opacity-50 cursor-not-allowed hover:bg-transparent"
        : "";

    const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${disabledClasses}
    ${className}
  `.trim();

    const handleClick = () => {
        if (!disabled && onClick) {
            onClick();
        }
    };

    if (href) {
        return (
            <a href={href} className={buttonClasses} onClick={handleClick}>
                {children}
            </a>
        );
    }

    return (
        <button className={buttonClasses} onClick={handleClick} disabled={disabled}>
            {children}
        </button>
    );
}
